import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { Filter } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';
import { useTaskTablesContext } from '../../context';

export const SourceFilter: FC = () => {
  const [t] = useTranslation(['backend', 'common']);
  const filters = useTaskTablesContext('filters$');
  const { updateFilter, sourcePrefix, setSourcePrefix, sourceRequest } = useTaskTablesContext();

  const options = useSubscribe(
    sourceRequest.data$,
    (response) => response?.data.items.flatMap((item) => (item.id ? { label: item.name ?? '', value: item.id } : [])) ?? [],
  );
  const total = useSubscribe(sourceRequest.data$, (response) => response?.data.total ?? 0);
  const loading = useSubscribe(sourceRequest.pending$);

  const handleChange = useCallbackRef((option: Option<string> | null) => {
    updateFilter('source', option);
  });

  const serverSideFiltering = useMemo(
    () => ({ filter: sourcePrefix, setFilter: setSourcePrefix, total }),
    [setSourcePrefix, sourcePrefix, total],
  );

  return (
    <Filter<string>
      label={t('common:SOURCE')}
      options={options}
      selected={filters.source}
      onChange={handleChange}
      loading={loading}
      serverSideFiltering={serverSideFiltering}
      isClearable
      dataTestId='source-filter'
    />
  );
};
